import { useCallback, useEffect, useRef, useState } from "react";
import { MessageCircle, X, Send, Sparkles } from "lucide-react";
import { API } from "@/lib/api";
import { useTranslation } from "react-i18next";

const genMessageId = () => `msg_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

export default function Chatbot() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState(() => [
    { id: genMessageId(), role: "assistant", content: t("chatbot.greeting") },
  ]);
  const [input, setInput] = useState("");
  const [streaming, setStreaming] = useState(false);
  const [sessionId] = useState(() => `sess_${Math.random().toString(36).slice(2)}`);
  const scrollRef = useRef(null);

  // Refresh greeting when language changes and no user has spoken
  useEffect(() => {
    setMessages((m) =>
      m.length === 1 ? [{ id: genMessageId(), role: "assistant", content: t("chatbot.greeting") }] : m
    );
  }, [i18n.language, t]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, streaming]);

  const send = useCallback(async () => {
    const text = input.trim();
    if (!text || streaming) return;
    const assistantId = genMessageId();
    setInput("");
    setMessages((m) => [
      ...m,
      { id: genMessageId(), role: "user", content: text },
      { id: assistantId, role: "assistant", content: "" },
    ]);
    setStreaming(true);

    const errorMsg = "Sorry — I'm briefly unavailable. Please email info@scitygate.com.";
    const appendToAssistant = (chunk) =>
      setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, content: msg.content + chunk } : msg)));
    const replaceAssistant = (content) =>
      setMessages((m) => m.map((msg) => (msg.id === assistantId ? { ...msg, content } : msg)));

    try {
      const res = await fetch(`${API}/chat/stream`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId, message: text, lang: i18n.language }),
      });
      if (!res.body) throw new Error("no stream");
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parts = buffer.split("\n\n");
        buffer = parts.pop() || "";
        for (const p of parts) {
          const line = p.replace(/^data: ?/, "");
          if (line === "[DONE]") continue;
          if (line.startsWith("[error]")) {
            replaceAssistant(errorMsg);
            continue;
          }
          appendToAssistant(line);
        }
      }
    } catch {
      replaceAssistant(errorMsg);
    } finally {
      setStreaming(false);
    }
  }, [input, streaming, sessionId, i18n.language]);

  const onSubmit = useCallback((e) => { e.preventDefault(); send(); }, [send]);

  return (
    <>
      <button
        onClick={() => setOpen((v) => !v)}
        className="fixed bottom-20 right-5 z-[60] w-14 h-14 rounded-full bg-gradient-to-br from-gold-300 to-gold-600 text-neutral-950 shadow-2xl flex items-center justify-center hover:scale-105 transition-transform"
        aria-label="Chat"
        data-testid="chatbot-toggle"
      >
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-6 h-6" />}
        {!open && <span className="absolute -top-1 -right-1 w-3 h-3 bg-emerald-500 rounded-full animate-pulse" />}
      </button>

      {open && (
        <div className="fixed bottom-40 right-5 z-[60] w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-11rem)] bg-background border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="chatbot-panel">
          <div className="bg-neutral-950 text-white px-5 py-4 flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gold-300 to-gold-600 flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-neutral-950" />
            </div>
            <div>
              <div className="font-display font-semibold text-sm">{t("chatbot.title")}</div>
              <div className="text-[10px] uppercase tracking-widest text-gold-400">{t("chatbot.status")}</div>
            </div>
          </div>
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-4 space-y-3 bg-muted/30">
            {messages.map((m, idx) => (
              <ChatBubble key={m.id} message={m} isLast={idx === messages.length - 1} streaming={streaming} />
            ))}
          </div>
          <form onSubmit={onSubmit} className="border-t border-border p-3 flex gap-2 bg-background">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t("chatbot.placeholder")}
              disabled={streaming}
              className="flex-1 bg-muted rounded-full px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gold-400"
              data-testid="chatbot-input"
            />
            <button type="submit" disabled={streaming || !input.trim()} className="w-10 h-10 rounded-full bg-gold-400 hover:bg-gold-300 text-neutral-950 flex items-center justify-center disabled:opacity-50" data-testid="chatbot-send">
              <Send className="w-4 h-4" />
            </button>
          </form>
        </div>
      )}
    </>
  );
}

function ChatBubble({ message, isLast, streaming }) {
  const isUser = message.role === "user";
  const showPlaceholder = !message.content && streaming && isLast;
  return (
    <div className={`flex ${isUser ? "justify-end" : "justify-start"}`}>
      <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${isUser ? "bg-neutral-900 text-white dark:bg-white dark:text-neutral-900" : "bg-background border border-border"}`}>
        {message.content || (showPlaceholder ? "…" : "")}
      </div>
    </div>
  );
}
